import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    signal,
    TemplateRef,
    untracked,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { GridsService } from "../../services/grids.service";
import { S25ItemComponent } from "../s25-item/s25.item.component";
import { Grid } from "../s25-virtual-grid/s25.virtual.grid";
import { AcademicGrid } from "./s25.academic.grid";
import { Bind } from "../../decorators/bind.decorator";
import { Item } from "../../pojo/Item";
import { DowGrid, S25DowGridComponent } from "../s25-virtual-grid/s25.dow.grid.component";
import { S25AcademicGridsFilterComponent } from "./s25.academic.grids.filter.component";
import { S25Util } from "../../util/s25-util";
import { ModalConfig, S25ModalComponent } from "../s25-modal/s25.modal.component";
import { Throttle } from "../../decorators/debounce.decorator";
import { AvailService, EventPerm } from "../../services/avail.service";
import { PreferenceService } from "../../services/preference.service";
import { S25DatetimePrefssUtil } from "../s25-datetime-prefs/s25.datetime.prefs.util";
import { AriaLive } from "../../services/aria.live.service";
import { Sheet, SpreadSheetService } from "../s25-spreadsheet/spreadsheet.service";
import { EventBulkSaveService } from "../../services/event.bulk.save.service";
import { AvailabilityBatchRequest, Relationships, SpaceService } from "../../services/space.service";
import { Proto } from "../../pojo/Proto";
import ResultObj = Proto.ResultObj;
import { ReservationService } from "../../services/reservation.service";
import { Security } from "../../pojo/Security";
import Perms = Security.Perms;
import { s25Resource } from "../../util/s25.resource";
import { UserStore } from "../../stores/user.store";
import { S25VirtualGridModule } from "../s25-virtual-grid/s25.virtual.grid.module";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { DecimalPipe, NgTemplateOutlet } from "@angular/common";
import { PopoverComponent } from "../s25-popover/popover.component";
import { S25LoadingInlineModule } from "../s25-loading/s25.loading.inline.module";
import { S25ModalModule } from "../s25-modal/s25.modal.module";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25ConflictsModule } from "../s25-conflicts/s25.conflicts.module";
import { S25SimpleCollapseModule } from "../s25-simple-collapse/s25.simple.collapse.module";
import { S25AcademicGridItemComponent } from "./s25.academic.grid.item.component";
import { S25ClickOutsideDirective } from "../s25-click-outside/s25.click.outside.directive";
import { S25SelectComponent } from "../../standalone/s25.select.component";
import { S25AcademicGridsLegendComponent } from "./s25.academic.grids.legend.component";
import { PluralSPipe } from "../../pipes/s25.pipes";
import { S25AcademicGridsShareComponent } from "./s25.academic.grids.share.component";
import { S25AcademicGridsWarningsMenuComponent } from "./s25.academic.grids.warnings.menu.component";
import { AcademicGridsWarningIconComponent } from "./s25.academic.grids.warning.icon.component";
import { AcademicGridsStore } from "./s25.academic.grids.store";
import { AcademicGridStore } from "./s25.academic.grid.store";
import { SheetEntry } from "../s25-spreadsheet/types/sheet.js.types.expanded";
import {
    TypedTemplateContextDirective,
    TypedTemplateDirective,
    VoidTemplateDirective,
} from "../../standalone/typed.template.directive";
import IssueTypeId = AcademicGrid.IssueTypeId;
import { S25AcademicGridActiveFiltersComponent } from "./s25.academic.grid.active.filters.component";
import { DropDownItem } from "../../pojo/DropDownItem";
import { DowGridUtil } from "../s25-virtual-grid/s25.dow.grid.util";
import { S25Assert } from "../../util/s25.assert";
import { FlsStore } from "../../stores/fls.store";
import { S25AcademicGridItemTooltipComponent } from "./s25.academic.grid.item.tooltip.component";
import { S25Array } from "../../util/core/s25.array";
import { S25Error } from "../../util/core/s25.error";

@Component({
    selector: "s25-ng-academic-grid",
    template: `
        <h2 class="grid-name ngCenter">{{ grid()!.name }}</h2>
        @if (store.status.isLoading()) {
            <s25-ng-loading-inline-static />
        } @else {
            <s25-ng-dow-pattern-grid
                #dowGrid
                [items]="store.items()"
                [rowHeaders]="store.rows()"
                [displayMessage]="store.message()"
                [interactionHandlers]="interactionHandlers"
                [domainEquals]="domainEquals"
                [itemInitialized]="onItemInitialized"
                [itemProjector]="itemProjector"
                [dows]="store.allDows()"
                [visibleDows]="store.visibleDows()"
                [hasVisibleDowSelector]="false"
                [startHour]="store.startHour()"
                [endHour]="store.endHour()"
                [canDrag]="canDrag()"
                [hasUndo]="store.canEditX() || store.canEditY()"
                [displayShadows]="store.hasShadows()"
                [hasMinimap]="true"
                [hasStandardSchedule]="true"
                [(standardSchedule)]="store.standardSchedule"
                [selectedSchedule]="store.defaultSchedule()"
                [hasSnapToStandardSchedules]="true"
                [snapToStandardSchedules]="true"
                [optionsLeftTemplate]="optionsLeftTmpl()"
                [optionsRightTemplate]="optionsRightTmpl()"
                [optionsBelowTemplate]="optionsBelowTmpl()"
                [rowHeaderTemplate]="rowHeaderTmpl()"
                [itemTemplate]="itemTmpl()"
                [decorationTemplate]="decorationTmpl()"
                [spanHours]="3"
                [findOverlap]="true"
                [createOverlapItems]="true"
                [resizeOnDowChange]="true"
                [dragParticipantsResolver]="dragParticipantsResolver"
                [frozenRows]="store.frozenRows()"
            />
        }

        <ng-template #optionsLeft [template]="optionsLeftTmpl()" let-defaultOptions="defaultOptions">
            <button class="aw-button aw-button--danger--outline exit" (click)="gridsStore.exitGrid()">Exit</button>
            @if (grid()!.isOwner) {
                <s25-ng-button [type]="'outline'" (clicked)="shareModal.open()">Share</s25-ng-button>
            }
            <ng-container [ngTemplateOutlet]="defaultOptions" />
        </ng-template>

        <ng-template #optionsRight [template]="optionsRightTmpl()" let-defaultOptions="defaultOptions">
            @if (isLoading()) {
                <s25-ng-loading-inline-static [text]="'Saving...'" />
            }
            <ng-container [ngTemplateOutlet]="defaultOptions" />
            <s25-popover [popoverTemplate]="legend" [openTrigger]="'click'" [closeTrigger]="'click'">
                <s25-ng-button [type]="'outline'" class="legend">Legend</s25-ng-button>
            </s25-popover>
            <ng-template #legend void>
                <s25-ng-academic-grids-legend
                    [showLocResConflicts]="false"
                    [showReserved]="store.mode() === 'location'"
                />
            </ng-template>
            <s25-ng-button [type]="'danger--outline'" [onClick]="checkIssues">Run Checks</s25-ng-button>
            @if (store.saveType() === "bulk") {
                <s25-ng-button [type]="'primary'" [onClick]="save">Apply Changes</s25-ng-button>
            }
        </ng-template>

        <ng-template #optionsBelow void>
            <div class="optionsBelow">
                <div class="left">
                    <s25-ng-select [(value)]="store.mode" [options]="modeOptions" />
                    <s25-popover [popoverTemplate]="warningsMenu" [openTrigger]="'click'" [closeTrigger]="'click'">
                        <s25-ng-button [type]="'outline'" class="warnings-menu"> Warnings </s25-ng-button>
                    </s25-popover>
                    <ng-template #warningsMenu void>
                        <s25-ng-academic-grids-warnings-menu />
                    </ng-template>
                </div>
                <div class="right">
                    <s25-ng-academic-grid-active-filters />

                    <div class="filtersWrapper" s25-ng-click-outside (clickOutside)="toggleFilters(false)">
                        <button class="aw-button aw-button--outline toggle-filters" (click)="toggleFilters()">
                            <s25-ng-icon [type]="showFilters() ? 'caretUp' : 'caretDown'" [ariaLabel]="false" />
                            Filters
                        </button>
                        @if (showFilters()) {
                            <s25-ng-academic-grids-filter
                                (apply)="toggleFilters(false)"
                                (clear)="toggleFilters(false)"
                            />
                        }
                    </div>
                </div>
            </div>
        </ng-template>

        <ng-template #rowHeaderTemplate [template]="rowHeaderTmpl()" let-header="header">
            <div class="row-header" [attr.data-id]="header.academicGrid[store.mode()]?.id">
                @if (header.academicGrid[store.mode()]?.id === "none") {
                    {{ header.academicGrid.instructor?.name ?? header.academicGrid.location?.name }}
                } @else if (store.mode() === "schedule" && !!header.academicGrid.schedule) {
                    <s25-item
                        [itemId]="header.academicGrid.schedule.eventId"
                        [itemTypeId]="Item.Ids.Event"
                        [itemName]="header.academicGrid.schedule.header"
                        [newTab]="true"
                        [popoverTemplate]="itemPopoverTemplate"
                    />

                    <ng-template #itemPopoverTemplate void>
                        <s25-ng-academic-grid-item-tooltip [data]="{ data: header.academicGrid.schedule }" />
                    </ng-template>
                } @else if (store.mode() === "instructor" && !!header.academicGrid.instructor) {
                    <s25-item
                        [itemId]="header.academicGrid.instructor.id"
                        [itemTypeId]="Item.Ids.Contact"
                        [itemName]="header.academicGrid.instructor.name"
                        [newTab]="true"
                    />
                } @else if (store.mode() === "location" && !!header.academicGrid.location) {
                    <s25-item
                        [itemId]="header.academicGrid.location.id"
                        [itemTypeId]="Item.Ids.Location"
                        [itemName]="header.academicGrid.location.name"
                        [newTab]="true"
                    />
                }
            </div>
        </ng-template>

        <ng-template
            #itemTemplate
            [template]="itemTmpl()"
            let-item="item"
            let-projection="projection"
            let-dragging="dragging"
            let-moving="moving"
        >
            <s25-ng-academic-grid-item
                [item]="item"
                [projection]="projection"
                [dragging]="dragging"
                [moving]="moving"
            />
        </ng-template>

        <ng-template #decorationTemplate [template]="decorationTmpl()" let-decoration="decoration">
            @if (decoration.data.type === "aggregate-overlap") {
                <div class="item overlapItem grid-color" data-border="overlapping"></div>
            }
        </ng-template>

        <ng-template #issuesCollapse [templateContext]="tmplIssues" let-issues="issues">
            @let defaultOpen = issues.counts.types === 1;
            @for (type of issues.types; track type.id) {
                @if (!!type.issues.length) {
                    <ng-template #headerTemplate>
                        <div class="issue-collapse-header">
                            <s25-ng-academic-grids-warning-icon [warningLevel]="type.action" />
                            @let count = (type.showCount ?? true) ? type.issues.length + " " : "";
                            <h3>{{ count + type.label | pluralS: type.issues.length }}</h3>
                        </div>
                    </ng-template>
                    <s25-simple-collapse
                        class="issues"
                        [attr.data-type]="type.id"
                        [expanded]="defaultOpen"
                        [header]="headerTemplate"
                    >
                        @for (iss of type.issues; track $index) {
                            <ng-container *ngTemplateOutlet="issue; context: { issue: iss, typeId: type.id }" />
                        }
                    </s25-simple-collapse>
                }
            }
        </ng-template>

        <ng-template #issue [templateContext]="tmplIssue" let-issue="issue" let-typeId="typeId">
            <div class="issue {{ typeId }}">
                @if (typeId === "instructorConflicts") {
                    <label>Instructor:</label>
                    <span>{{ issue.item.academicGrid.effective.instructors()[0]?.name }}</span>
                    <label>Meeting Pattern:</label>
                    <span>{{ issue.item.academicGrid.effective.position.dow() }}</span>
                    <label>Time:</label>
                    <span>{{ issue.time }}</span>
                    <label>Sections:</label>
                    <ul>
                        @for (item of issue.items; track item.id) {
                            <li>{{ item.academicGrid.eventName }}</li>
                        }
                    </ul>
                } @else if (typeId === "locationConflicts") {
                    <label>Meeting Pattern:</label>
                    <span>{{ issue.item.academicGrid.effective.position.dow() }}</span>
                    <label>Time:</label>
                    <span>{{ issue.time }}</span>
                    <label>Section:</label>
                    <s25-item
                        [itemTypeId]="Item.Ids.Event"
                        [itemId]="issue.item.academicGrid.eventId"
                        [itemName]="issue.item.academicGrid.eventName"
                    />
                    <label>Conflicts With:</label>
                    <s25-item [itemTypeId]="Item.Ids.Event" [itemId]="issue.events![0]" />
                    <label>In Location:</label>
                    <s25-item [itemTypeId]="Item.Ids.Location" [itemId]="issue.locations![0]" />
                } @else if (typeId === "resourceConflicts") {
                    <label>Meeting Pattern:</label>
                    <span>{{ issue.item.academicGrid.effective.position.dow() }}</span>
                    <label>Time:</label>
                    <span>{{ issue.time }}</span>
                    <label>Section:</label>
                    <s25-item
                        [itemTypeId]="Item.Ids.Event"
                        [itemId]="issue.item.academicGrid.eventId"
                        [itemName]="issue.item.academicGrid.eventName"
                    />
                    <label>Conflicts With:</label>
                    <s25-item [itemTypeId]="Item.Ids.Event" [itemId]="issue.events![0]" />
                    <label>For Resource:</label>
                    <s25-item [itemTypeId]="Item.Ids.Resource" [itemId]="issue.resources![0]" />
                } @else if (typeId === "alsoAssigns") {
                    <label>Meeting Pattern:</label>
                    <span>{{ issue.item.academicGrid.effective.position.dow() }}</span>
                    <label>Time:</label>
                    <span>{{ issue.time }}</span>
                    <label>Section:</label>
                    <span>{{ issue.item.academicGrid.eventName }}</span>
                    <label>Also assigned:</label>
                    <ul class="bullets">
                        @for (location of issue.locations; track location) {
                            <li><s25-item [itemTypeId]="Item.Ids.Location" [itemId]="location" /></li>
                        }
                    </ul>
                } @else if (typeId === "orphanedAlsoAssigns") {
                    <label>Meeting Pattern:</label>
                    <span>{{ issue.item.academicGrid.effective.position.dow() }}</span>
                    <label>Time:</label>
                    <span>{{ issue.time }}</span>
                    <label>Section:</label>
                    <span>{{ issue.item.academicGrid.eventName }}</span>
                    <label>Orphaned Also Assigns:</label>
                    <ul class="bullets">
                        @for (location of issue.locations; track location) {
                            <li><s25-item [itemTypeId]="Item.Ids.Location" [itemId]="location" /></li>
                        }
                    </ul>
                } @else if (typeId === "saveMessages" || typeId === "saveErrors") {
                    <label>Meeting Pattern:</label>
                    <span>{{ issue.item.academicGrid.effective.position.dow() }}</span>
                    <label>Time:</label>
                    <span>{{ issue.time }}</span>
                    <label>Section:</label>
                    <span>{{ issue.item.academicGrid.eventName }}</span>
                    @if (typeId === "saveErrors") {
                        <label>Summary:</label>
                        <span>{{ issue.message }}</span>
                    }
                    <label>Messages:</label>
                    <ul class="bullets">
                        @for (message of issue.messages; track message) {
                            <li>{{ message }}</li>
                        }
                    </ul>
                } @else if (typeId === "locationUndersized" || typeId === "locationOversized") {
                    <label>Meeting Pattern:</label>
                    <span>{{ issue.item.academicGrid.effective.position.dow() }}</span>
                    <label>Time:</label>
                    <span>{{ issue.time }}</span>
                    <label>Section:</label>
                    <span>{{ issue.item.academicGrid.eventName }}</span>
                    <label>Location:</label>
                    <span>{{ issue.location!.name }}</span>
                    <label>{{ issue.headCountLabel }}:</label>
                    <span>{{ issue.headCount }}</span>
                    <label>Capacity:</label>
                    <span>{{ issue.location!.maxCapacity }}</span>
                    <label>Fill Ratio:</label>
                    <span>{{ (100 * issue.headCount!) / issue.location!.maxCapacity | number: "1.0-0" }}%</span>
                } @else if (typeId === "standardSchedules") {
                    <label>Meeting Pattern:</label>
                    <span>{{ issue.item.academicGrid.effective.position.dow() }}</span>
                    <label>Time:</label>
                    <span>{{ issue.time }}</span>
                    <label>Section:</label>
                    <span>{{ issue.item.academicGrid.eventName }}</span>
                } @else if (typeId === "missingFeaturePrefs") {
                    <label>Meeting Pattern:</label>
                    <span>{{ issue.item.academicGrid.effective.position.dow() }}</span>
                    <label>Time:</label>
                    <span>{{ issue.time }}</span>
                    <label>Section:</label>
                    <span>{{ issue.item.academicGrid.eventName }}</span>
                    <label>Location:</label>
                    <span>{{ issue.location!.name }}</span>
                    <label>Missing Features:</label>
                    <span>{{ issue.missingPrefs!.join(", ") }}</span>
                } @else if (typeId === "missingPartitionPrefs") {
                    <label>Meeting Pattern:</label>
                    <span>{{ issue.item.academicGrid.effective.position.dow() }}</span>
                    <label>Time:</label>
                    <span>{{ issue.time }}</span>
                    <label>Section:</label>
                    <span>{{ issue.item.academicGrid.eventName }}</span>
                    <label>Location:</label>
                    <span>{{ issue.location!.name }}</span>
                }
            </div>
        </ng-template>

        <s25-ng-modal #issuesModal [title]="'Issues'" [size]="'sm'">
            <ng-template #s25ModalBody [templateContext]="tmplIssues" let-issues="issues">
                @if (issues.counts.total === issues.counts.ignore) {
                    <p>There are no outstanding issues.</p>
                } @else {
                    <p>
                        There are currently
                        <span class="issue-count">{{ issues.counts.total - issues.counts.ignore }}</span> outstanding
                        issues
                    </p>
                    @if (issues.counts.warningLevels > 1) {
                        <div class="issue-counts">
                            @if (!!issues.counts.prevent) {
                                <span class="issue-count">{{ issues.counts.prevent }}</span>
                                <p>Preventing save</p>
                            }
                            @if (!!issues.counts.warn) {
                                <span class="issue-count">{{ issues.counts.warn }}</span>
                                <p>Warnings</p>
                            }
                            @if (!!issues.counts.info) {
                                <span class="issue-count">{{ issues.counts.info }}</span>
                                <p>For information only</p>
                            }
                        </div>
                    }
                    <ng-container *ngTemplateOutlet="issuesCollapse; context: { issues: issues }" />
                }
            </ng-template>
            <ng-template #s25ModalFooter let-issues="issues">
                <div style="display: flex; justify-content: end; gap: 0.5rem">
                    @if (issues.counts.total > 0) {
                        <s25-ng-button [type]="'outline'" [title]="'Download as Excel'" [onClick]="downloadIssues">
                            <s25-ng-icon [type]="'excel'" [ariaLabel]="'Download as Excel'" />
                        </s25-ng-button>
                    }
                    <s25-ng-button [type]="'primary'" (clicked)="issuesModal.close()">Ok</s25-ng-button>
                </div>
            </ng-template>
        </s25-ng-modal>

        <s25-ng-modal #shareModal [title]="'Share Grid'" [size]="'xs'">
            <ng-template #s25ModalBody>
                <s25-ng-share-academic-grid
                    [id]="grid()!.id"
                    [(sharedWith)]="grid()!.sharedWith"
                    (saved)="shareModal.close()"
                    (cancelled)="shareModal.close()"
                />
            </ng-template>
        </s25-ng-modal>

        <s25-ng-modal #confirmIssuesModal [title]="'Issues'" [type]="'confirm'" [size]="'xs'">
            <ng-template
                #s25ModalBody
                [templateContext]="tmplIssuesAction"
                let-issues="issues"
                let-action="action"
                let-message="message"
            >
                <div class="confirmation">
                    <p>
                        @if (!!message) {
                            {{ message }}
                        } @else {
                            This action would create {{ issues.counts.total }}
                            {{ "issue" | pluralS: issues.counts.total }}.
                        }
                    </p>
                    @if (action === "confirm") {
                        <p class="ngBold">Do you wish to continue?</p>
                    } @else if (action === "acknowledge") {
                        <p class="ngBold">Item will be returned to its original position</p>
                    }
                    <ng-container *ngTemplateOutlet="issuesCollapse; context: { issues: issues }" />
                </div>
            </ng-template>
        </s25-ng-modal>

        <s25-ng-modal #removeOrphanedAlsoAssignModal [title]="'Issues'" [size]="'xs'">
            <ng-template
                #s25ModalBody
                [templateContext]="tmplConfirmOrphanedAlsoAssign"
                let-issues="issues"
                let-count="count"
            >
                <div class="confirmation">
                    <p>
                        This action would orphan {{ count }} {{ "location" | pluralS: count }} which
                        {{ count > 1 ? "have" : "has an" }} "also assign" {{ "relationship" | pluralS: count }} with a
                        changed location.
                    </p>
                    <p class="ngBold">
                        Do you wish remove {{ count > 1 ? "these" : "this" }} orphaned
                        {{ "location" | pluralS: count }}?
                    </p>
                    <ng-container *ngTemplateOutlet="issuesCollapse; context: { issues: issues }" />
                </div>
            </ng-template>

            <ng-template #s25ModalFooter>
                <div class="modalFooter">
                    <s25-ng-button [type]="'danger--outline'" (click)="removeOrphanedAlsoAssignModal.close(true)">
                        Remove
                    </s25-ng-button>
                    <s25-ng-button [type]="'outline'" (click)="removeOrphanedAlsoAssignModal.close(false)">
                        Keep
                    </s25-ng-button>
                </div>
            </ng-template>
        </s25-ng-modal>

        <s25-ng-modal #saveMessagesModal [type]="'acknowledge'" [size]="'xs'">
            <ng-template #s25ModalBody let-issues="issues">
                <ng-container *ngTemplateOutlet="issuesCollapse; context: { issues: issues }" />
            </ng-template>
        </s25-ng-modal>

        <s25-ng-modal #offerSwapModal [title]="'Swap Locations'" [type]="'confirm'" [size]="'xs'">
            <ng-template #s25ModalBody [templateContext]="tmplSwap" let-swap="swap">
                <p class="ngBold">Do you want to swap the locations of these sections?</p>
                <div class="locationSwap">
                    <span>{{ swap.sectionA }}</span>
                    <s25-ng-icon [type]="'arrowRight'" [ariaLabel]="'goes to location'" />
                    <span class="location">{{ swap.locationB }}</span>
                    <span>{{ swap.sectionB }}</span>
                    <s25-ng-icon [type]="'arrowRight'" [ariaLabel]="'goes to location'" />
                    <span class="location">{{ swap.locationA }}</span>
                </div>
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        :host {
            --item-border-color: #333;
            --item-border-width: 1px;
            --item-border: var(--item-border-width) solid var(--item-border-color);
            --item-shadow-background: #616161;
            --column-width: 50px;
        }

        ::ng-deep .nm-party--on s25-ng-academic-grid {
            --item-border-color: #000;
        }

        s25-ng-loading-inline-static {
            text-align: center;
        }

        /* Space conflicts render behind real events (which default to z-index 1) */
        ::ng-deep s25-ng-academic-grid .grid--item:has(.item.locationReserved) {
            z-index: 0;
        }

        ::ng-deep s25-ng-academic-grid .grid--underlays .grid--item {
            z-index: 0;
        }

        .overlapItem {
            z-index: 10;
            position: relative;
            height: calc(100% - 3 * var(--border-width));
            margin: calc(0px - var(--grid-color--border-width));
            box-sizing: content-box;
        }

        :host.disableGrid {
            opacity: 0.5;
            pointer-events: none;
        }

        .optionsBelow {
            display: flex;
            justify-content: space-between;
            gap: 0.5rem;
            padding-block-end: 0.5rem;
        }

        .optionsBelow .left {
            display: flex;
            justify-content: start;
            gap: 0.5rem;
        }

        .optionsBelow .right {
            display: flex;
            justify-content: end;
            gap: 0.5rem;
        }

        .filtersWrapper {
            position: relative;
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }

        s25-ng-academic-grids-filter {
            position: absolute;
            z-index: 11;
            top: 100%;
            right: 0;
            margin-top: 0.5rem;
            padding: 1rem;
            box-shadow:
                0 7px 14px 0 rgba(50, 50, 93, 0.1),
                0 3px 6px 0 rgba(0, 0, 0, 0.08);
            border-radius: 0.5rem;
        }

        .row-header {
            padding-inline-start: 0.5rem;

            &:not([data-id="none"]) {
                cursor: pointer;
            }
        }

        .issues {
            margin-top: 1rem;
            display: block;
        }

        .issue {
            display: grid;
            grid-template-columns: 10em auto;
            padding: 0.5em;
            border-block: 1px solid var(--border-color);
            background: color-mix(in lch, var(--simple-collapse--highlight-color), #fff0 92%);
            margin-top: 1rem;
            overflow-wrap: anywhere;
        }

        .issue:last-child {
            border-bottom: none;
        }

        .issue label {
            font-weight: bold;
        }

        .issue ul {
            list-style: none;
            padding: 0;
            margin: 0;

            &.bullets {
                list-style: initial;
            }
        }

        .issue-collapse-header {
            display: flex;
            padding-left: calc(3px + 0.5rem);
        }

        .issue-counts {
            display: grid;
            grid-template-columns: auto 1fr;
            column-gap: 0.5rem;
            padding-bottom: 1rem;
        }

        .issue-count {
            font-weight: bold;
        }

        .issue s25-ng-conflicts {
            grid-column: 1/-1;
        }

        ::ng-deep s25-ng-academic-grid .simple-collapse--wrapper {
            border: none;
            box-shadow: none;
            background: none !important;
        }

        s25-simple-collapse.issues {
            --simple-collapse--bg-color: var(--color--surface-bg);

            &[data-type="standardSchedules"] {
                --simple-collapse--highlight-color: #ff5959;
            }

            &[data-type="instructorConflicts"] {
                --simple-collapse--highlight-color: #ba92e1;
            }

            &[data-type="locationConflicts"],
            &[data-type="locationUndersized"],
            &[data-type="locationOversized"],
            &[data-type="alsoAssigns"],
            &[data-type="orphanedAlsoAssigns"] {
                --simple-collapse--highlight-color: #0077d7;
            }

            &[data-type="resourceConflicts"] {
                --simple-collapse--highlight-color: #e91e63;
            }

            &[data-type="saveMessages"] {
                --simple-collapse--highlight-color: #f79232;
            }

            &[data-type="saveErrors"] {
                --simple-collapse--highlight-color: var(--error-message-color);
            }

            &[data-type="missingFeaturePrefs"] {
                --simple-collapse--highlight-color: #ffff39;
            }

            &[data-type="missingPartitionPrefs"] {
                --simple-collapse--highlight-color: #78ce3d;
            }
        }

        .overlapItem {
            height: calc(100% - 3 * var(--border-width));
            translate: 0 var(--border-width);
            border: var(--grid-color--border, var(--item-border));
        }

        /* issue-collapse-header is listed twice for increased specificity */
        .issue-collapse-header.issue-collapse-header h3 {
            padding-inline-start: 0.5rem;
            margin: 0;
            padding-block: 6px;
            font-weight: 600;
            font-size: 1rem !important;
        }

        .modalFooter {
            display: flex;
            gap: 0.5rem;
            justify-content: flex-end;
        }

        .locationSwap {
            display: grid;
            grid-template-columns: auto min-content 1fr;
            row-gap: 0.25rem;
            column-gap: 0.5rem;
            margin-top: 0.5rem;

            s25-ng-icon {
                font-size: 0.8em;
                align-self: center;
            }

            .location {
                font-style: italic;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: true,
    imports: [
        S25LoadingInlineModule,
        S25VirtualGridModule,
        S25ButtonComponent,
        NgTemplateOutlet,
        PopoverComponent,
        S25ModalModule,
        S25IconComponent,
        S25ConflictsModule,
        S25SimpleCollapseModule,
        S25AcademicGridItemComponent,
        S25ItemComponent,
        S25AcademicGridsFilterComponent,
        S25ClickOutsideDirective,
        S25SelectComponent,
        S25AcademicGridsLegendComponent,
        PluralSPipe,
        S25AcademicGridsShareComponent,
        S25AcademicGridsWarningsMenuComponent,
        AcademicGridsWarningIconComponent,
        TypedTemplateContextDirective,
        TypedTemplateDirective,
        VoidTemplateDirective,
        DecimalPipe,
        S25AcademicGridActiveFiltersComponent,
        S25AcademicGridItemTooltipComponent,
    ],
    providers: [AcademicGridStore],
})
export class S25AcademicGridComponent {
    readonly grid = computed(() => this.store.grid());
    protected readonly canDrag = computed(() => ({
        x: this.store.canEditX(),
        y: this.store.canEditY(),
    }));

    // Views
    private readonly dowGridRef = viewChild<
        string,
        S25DowGridComponent<
            AcademicGrid.Row,
            AcademicGrid.SourceItem,
            AcademicGrid.ProjectionData,
            DowGrid.DecorationData
        >
    >("dowGrid", { read: S25DowGridComponent });
    private readonly issuesModal = viewChild<string, S25ModalComponent<void, { issues: AcademicGrid.Issues }>>(
        "issuesModal",
        { read: S25ModalComponent },
    );
    private readonly confirmIssuesModal = viewChild<string, S25ModalComponent<boolean, typeof this.tmplIssuesAction>>(
        "confirmIssuesModal",
        {
            read: S25ModalComponent,
        },
    );
    private readonly removeOrphanedAlsoAssignModal = viewChild<
        string,
        S25ModalComponent<boolean, typeof this.tmplConfirmOrphanedAlsoAssign>
    >("removeOrphanedAlsoAssignModal", {
        read: S25ModalComponent,
    });
    private readonly saveMessagesModal = viewChild<string, S25ModalComponent<void, { issues: AcademicGrid.Issues }>>(
        "saveMessagesModal",
        { read: S25ModalComponent },
    );
    private readonly offerSwapModal = viewChild<string, S25ModalComponent<boolean, typeof this.tmplSwap>>(
        "offerSwapModal",
        { read: S25ModalComponent },
    );

    protected readonly optionsLeftTmpl = viewChild<TemplateRef<DowGrid.WrappedOptionsTemplateContext>>("optionsLeft");
    protected readonly optionsRightTmpl = viewChild<TemplateRef<DowGrid.OptionsTemplateContext>>("optionsRight");
    protected readonly optionsBelowTmpl = viewChild<TemplateRef<void>>("optionsBelow");
    protected readonly rowHeaderTmpl =
        viewChild<TemplateRef<Grid.HeaderTemplateContext<AcademicGrid.Row>>>("rowHeaderTemplate");
    protected readonly itemTmpl =
        viewChild<TemplateRef<DowGrid.ItemProjectionTemplateContext<AcademicGrid.Item, AcademicGrid.ProjectionData>>>(
            "itemTemplate",
        );
    protected readonly decorationTmpl =
        viewChild<TemplateRef<DowGrid.DecorationTemplateContext<AcademicGrid.Item, AcademicGrid.ProjectionData>>>(
            "decorationTemplate",
        );

    // Injectables
    private readonly userStore = inject(UserStore);
    protected readonly gridsStore = inject(AcademicGridsStore);
    protected readonly store = inject(AcademicGridStore);
    protected readonly flsStore = inject(FlsStore);
    private readonly eventBulkSaveService = inject(EventBulkSaveService);

    // Template contexts
    protected readonly tmplIssuesAction: {
        issues: AcademicGrid.Issues;
        action: "confirm" | "acknowledge";
        message?: string;
    };
    protected readonly tmplConfirmOrphanedAlsoAssign: { issues: AcademicGrid.Issues; count: number };
    protected readonly tmplIssues: { issues: AcademicGrid.Issues };
    protected readonly tmplIssue: {
        issue: AcademicGrid.Issue;
        typeId: IssueTypeId | "alsoAssigns" | "orphanedAlsoAssigns";
    };
    protected readonly tmplSwap: { swap: { sectionA: string; sectionB: string; locationA: string; locationB: string } };

    // Resources
    private readonly instanceTz = s25Resource({ loader: () => PreferenceService.getPreferences(["timezone"], "S") });

    // Variables
    private readonly timezone = computed(() => ({
        instance: S25DatetimePrefssUtil.timeZoneToIANA[this.instanceTz.value()?.timezone?.value || 25],
        user: this.userStore.timezone(),
    }));
    protected readonly interactionHandlers: DowGrid.InteractionHandlers<AcademicGrid.Item> = {
        onPickup: this.onPickup,
        onDragChanged: this.onDragChanged,
        onDrop: this.loadWhile(this.onDrop),
        onCommit: this.onCommit,
        onUndo: this.loadWhile(this.onUndo),
        onRedo: this.loadWhile(this.onRedo),
    };
    protected readonly showFilters = signal(false);
    protected readonly modeOptions = [
        { value: "schedule", label: "Standard View" },
        { value: "instructor", label: "Instructor View" },
        { value: "location", label: "Location View" },
    ] as const satisfies { value: AcademicGrid.Mode; label: string }[];
    private issues: AcademicGrid.Issues;
    protected readonly isLoading = signal(false);

    protected readonly Item = Item;
    private readonly elementRef = inject(ElementRef);

    constructor() {
        this.store.setGrid(this.gridsStore.grid());

        effect(() => {
            if (this.store.status.hasError()) {
                alert("Something went wrong when fetching grid data");
                this.gridsStore.exitGrid();
            }
        });

        // Rows are hidden imperatively per mode, so the rows of the view being switched to are stale
        // until the filters are applied to them
        effect(() => {
            this.store.mode();
            untracked(() => this.applyFilters());
        });

        effect(() => this.dowGridRef()?.bringToFront(this.store.broughtToFront()));
    }

    @Bind
    protected onItemInitialized(item: AcademicGrid.Item) {
        item.academicGrid.initialized.set(item);
    }

    @Bind
    /**
     * Projects the single item into multiple places on the grid as necessary.
     * If the mode is "location", the item will be projected into all assigned locations
     */
    protected itemProjector(
        item: AcademicGrid.Item,
    ): readonly DowGrid.ItemProjectionDefinition<AcademicGrid.ProjectionData>[] {
        const mode = this.store.mode();
        switch (mode) {
            case "schedule":
                return DowGridUtil.primaryProjection(item, {
                    placement: this.placement(item, () => String(item.academicGrid.profileId)),
                    is24Hours: this.userStore.is24Hours,
                });
            case "instructor":
                return this.itemProjectorInstructorView(item);
            case "location":
                return this.itemProjectorLocationView(item);
            default:
                S25Assert.never(mode, "uncovered mode");
        }
    }

    private itemProjectorInstructorView(
        item: AcademicGrid.Item,
    ): readonly DowGrid.ItemProjectionDefinition<AcademicGrid.ProjectionData>[] {
        const instructors = untracked(item.academicGrid.effective.position.instructors);
        const [projection] = DowGridUtil.primaryProjection(item, {
            placement: this.placement(item, () => String(instructors[0] ?? "none")),
            is24Hours: this.userStore.is24Hours,
        });
        return [{ ...projection, data: { ...projection.data, instructorId: instructors[0] ?? "none" } }];
    }

    private placement(item: AcademicGrid.Item, rowKey: () => string) {
        return computed(() => {
            const position = item.dowGrid.effective();
            const offset = item.dowGrid.candidate.offset();
            const height = 100 / this.store.rowIndex().size;
            const row = this.store.rowIndex().get(rowKey());
            return {
                dow: position.dow,
                startHour: position.startHour,
                endHour: position.endHour,
                top: (row === undefined ? AcademicGrid.offGridTop : row * height) + (offset?.y ?? 0),
                height,
            };
        });
    }

    private itemProjectorLocationView(
        item: AcademicGrid.Item,
    ): readonly DowGrid.ItemProjectionDefinition<AcademicGrid.ProjectionData>[] {
        const projections: DowGrid.ItemProjectionDefinition<AcademicGrid.ProjectionData>[] = [];

        // Conflicts also carry the rooms their event occupies outside this grid so the tooltip can name them.
        // Only rooms with a row can be placed, so an item occupying none of them is not on this grid at all.
        const locations = item.academicGrid.current().position.locations;
        const rowLocations = locations.filter((locationId) => this.store.rowIndex().has(String(locationId)));
        const locationIds: (number | "none")[] = locations.length ? rowLocations : ["none"];

        const dow = computed(() => item.academicGrid.effective.position.dow());
        const startHour = computed(() => item.academicGrid.effective.position.startHour());
        const endHour = computed(() => item.academicGrid.effective.position.endHour());
        const height = computed(() => 100 / this.store.rowIndex().size);
        for (const locationId of locationIds) {
            const key = `${item.id}-additional-location-${locationId}`;
            const occupancyRowId = computed(() => {
                const alsoAssigned = new Set(item.academicGrid.candidateAlsoAssign());
                const effective = item.academicGrid.effective.position
                    .locations()
                    .filter((id) => !alsoAssigned.has(id));
                return AcademicGrid.occupancyRowFromMembership(
                    locationId,
                    item.academicGrid.current().position.locations,
                    effective,
                );
            });
            projections.push({
                key,
                role: "additional-location",
                data: {
                    locationId,
                    dow,
                    startHour,
                    endHour,
                    height,
                    top: computed(() => {
                        const rowPosition = this.store.rowIndex().get(String(occupancyRowId()));
                        return rowPosition === undefined ? AcademicGrid.offGridTop : rowPosition * height();
                    }),
                },
                ariaLabel: computed(() => item.ariaLabel?.() ?? ""),
                hidden: computed(() => item.hidden?.() ?? false),
                noInteraction: computed(() => item.noInteraction?.() ?? false),
                minimapColor: computed(() => item.minimapColor?.()),
                noShadows: computed(() => occupancyRowId() === "none"),
            });
        }

        // Get also assign projections
        for (const locationId of item.academicGrid.candidateAlsoAssign()) {
            const key = `${item.id}-also-assign-location-${locationId}`;
            projections.push({
                key,
                role: "also-assign-location",
                data: {
                    locationId,
                    dow,
                    startHour,
                    endHour,
                    height,
                    top: computed(() => {
                        const rowPosition = this.store.rowIndex().get(String(locationId));
                        return rowPosition === undefined ? AcademicGrid.offGridTop : rowPosition * height();
                    }),
                },
                ariaLabel: computed(() => item.ariaLabel?.() ?? ""),
                hidden: computed(() => item.hidden?.() ?? false),
                noInteraction: computed(() => item.noInteraction?.() ?? false),
                minimapColor: computed(() => item.minimapColor?.()),
                noShadows: signal(false),
            });
        }

        // Get locations which need to show conflicts due to relationships
        const conflictLocationIds = AcademicGrid.getConflictLocations(
            this.store.locationRelationships(),
            item.academicGrid.effective.position.locations(),
        ).filter((id) => !!this.store.locations()[id]);

        for (const locationId of conflictLocationIds) {
            const key = `${item.id}-related-location-conflict-${locationId}`;
            projections.push({
                key,
                role: "related-location-conflict",
                data: {
                    locationId,
                    dow,
                    startHour,
                    endHour,
                    height,
                    top: computed(() => {
                        const rowPosition = this.store.rowIndex().get(String(locationId));
                        return rowPosition === undefined ? AcademicGrid.offGridTop : rowPosition * height();
                    }),
                },
                ariaLabel: computed(() => item.ariaLabel?.() ?? ""),
                hidden: computed(() => item.hidden?.() ?? false),
                noInteraction: computed(() => item.noInteraction?.() ?? false),
                minimapColor: signal("black"),
                noShadows: signal(false),
            });
        }

        return projections;
    }

    @Bind
    protected dragParticipantsResolver(
        primary: AcademicGrid.Item,
        items: readonly AcademicGrid.Item[],
    ): readonly AcademicGrid.Item[] {
        return primary.academicGrid.linkedItems;
    }

    protected readonly domainEquals = AcademicGrid.positionsEqual;

    @Bind
    private async onUndo(context: DowGrid.HistoryContext<AcademicGrid.Item>) {
        return this.dropAndClearOverrides(context);
    }

    @Bind
    private async onRedo(context: DowGrid.HistoryContext<AcademicGrid.Item>) {
        return this.dropAndClearOverrides(context);
    }

    private async dropAndClearOverrides(context: DowGrid.HistoryContext<AcademicGrid.Item>): Promise<boolean> {
        const [result, error] = await S25Util.Maybe(this.commitItems(context.participants));
        if (!result) this.clearCandidateOverrides(context.participants);
        if (error) throw error;
        return !!result;
    }

    private clearCandidateOverrides(items: readonly AcademicGrid.Item[]): void {
        for (const item of items) {
            item.academicGrid.removedOrphanedAlsoAssignLocations.set(new Set());
            item.academicGrid.swappedLocations.set(undefined);
        }
    }

    protected async toggleFilters(open?: boolean) {
        if (open !== undefined && open === this.showFilters()) return;
        await S25Util.delay(0); // Give dropdowns in the filter menu time to close and emit
        this.showFilters.set(open === undefined ? !this.showFilters() : open);
        if (!this.showFilters()) this.applyFilters();
    }

    @Bind
    public async applyFilters() {
        await this.disableWhile(() => {
            const mode = this.store.mode();
            if (!this.dowGridRef()) return;

            // Filter items
            const rowsToKeep = new Set<number>();
            const dowsToKeep = new Set<string>();
            const items = this.store.items();

            const itemFilters = this.store.filters().items.filters;
            const hasActiveItemFilter = itemFilters.some(
                (filter) => filter.keepRow !== false && filter.active() && (filter.showText?.() ?? true),
            );

            /* In the location view we want to keep all items as conflict markers, but make any item not matching
               the filter black like a conflict */
            for (const item of items) {
                if (item.academicGrid.isLocationConflict) continue;
                const keep = this.store
                    .filters()
                    .items.filters.every((filter) => !filter.active() || filter.filter(item));
                item.academicGrid.filteredOut.set(!keep);
                if (keep) {
                    dowsToKeep.add(item.academicGrid.current().position.dow);
                    switch (mode) {
                        case "schedule":
                            rowsToKeep.add(item.academicGrid.profileId);
                            break;
                        case "instructor":
                            for (const instructorId of item.academicGrid.current().position.instructors) {
                                rowsToKeep.add(instructorId);
                            }
                            break;
                        case "location":
                            if (hasActiveItemFilter) {
                                for (const loc of item.academicGrid.current().position.locations) rowsToKeep.add(loc);
                            }
                            break;
                        default:
                            S25Assert.never(mode, "uncovered mode");
                    }
                }
            }

            // Filter rows
            for (const row of this.store.rows()) {
                if (row.id === "none") continue;

                let keep = false;
                switch (mode) {
                    case "schedule":
                        keep = rowsToKeep.has(row.academicGrid.schedule!.profileId);
                        break;
                    case "instructor":
                        keep = rowsToKeep.has(<number>row.academicGrid.instructor!.id);
                        break;
                    case "location":
                        keep = rowsToKeep.has(<number>row.academicGrid.location!.id);
                        if (this.store.grid()?.locationQuery) {
                            keep ||= this.store
                                .filters()
                                .locations.filters.every((filter) => !filter.active() || filter.filter(row));
                        }
                        break;
                    default:
                        S25Assert.never(mode, "uncovered mode");
                }
                row.academicGrid.hidden.set(!keep);
            }

            // Filter dows
            const dowFilter = this.store.dowFilter();
            let visibleDows: string[];
            if (!dowFilter?.active()) {
                visibleDows = this.store.allDows().filter((dow) => dowsToKeep.has(dow));
            } else {
                const filteredDows = new Set(dowFilter.value().map((item: DropDownItem<string>) => item.itemId));
                visibleDows = this.store.allDows().filter((dow) => filteredDows.has(dow));
            }
            this.store.visibleDows.set(visibleDows);

            // Sort rows when section filters that keep matching rows are active
            if (mode === "location") {
                this.store.prioritizeLocations.set(hasActiveItemFilter ? rowsToKeep : undefined);
            }

            this.showFilters.set(false);
        });
    }

    @Bind
    protected async checkIssues() {
        const items = this.store.items().filter((item) => !item.academicGrid.isLocationConflict);
        const issues = await this.getIssues(items, true);
        if (issues.error) return;
        this.issues = issues.data!;
        await this.issuesModal()!.open({ templateData: { issues: this.issues } });
    }

    private async getIssues(
        items: readonly AcademicGrid.Item[],
        updateItems: boolean,
    ): Promise<Proto.ResultObj<AcademicGrid.Issues, true>> {
        const instructors = this.getInstructorConflicts(items);

        const changedItems = items.filter(AcademicGrid.hasItemChanged);
        const { data, error } = await this.getAvailabilityConflicts(changedItems, updateItems);
        if (error || !data) return { error: true };
        const { locations, resources } = data;

        // Downgrade location action to warn if all conflicts are overridable
        const onlyOverridableLocationConflicts =
            this.flsStore.canOverrideBlockedBy() &&
            locations.every((conflict) => conflict.relationshipType === Relationships.type.blockedBy);
        let locationAction = this.store.warningsConfig().locationConflicts.action;
        if (onlyOverridableLocationConflicts && locationAction === "prevent") locationAction = "warn";

        const locationUndersized = this.getLocationUndersizedIssues(items);
        const locationOversized = this.getLocationOversizedIssues(items);
        const standardSchedules = this.getStandardScheduleIssues(items);
        const missingFeaturePrefs = this.getMissingFeaturePrefsIssues(items);
        const missingPartitionPrefs = this.getMissingPartitionPrefsIssues(items);

        const types = (<AcademicGrid.IssueType[]>[
            {
                id: "instructorConflicts",
                label: AcademicGrid.itemIssueTypes.instructorConflicts.label,
                action: this.store.warningsConfig().instructorConflicts.action,
                issues: instructors,
            },
            {
                id: "locationConflicts",
                label: AcademicGrid.itemIssueTypes.locationConflicts.label,
                action: locationAction,
                issues: locations,
            },
            {
                id: "resourceConflicts",
                label: AcademicGrid.itemIssueTypes.resourceConflicts.label,
                action: this.store.warningsConfig().resourceConflicts.action,
                issues: resources,
            },
            {
                id: "locationUndersized",
                label: AcademicGrid.itemIssueTypes.locationUndersized.label,
                action: this.store.warningsConfig().locationUndersized.action,
                issues: locationUndersized,
            },
            {
                id: "locationOversized",
                label: AcademicGrid.itemIssueTypes.locationOversized.label,
                action: this.store.warningsConfig().locationOversized.action,
                issues: locationOversized,
            },
            {
                id: "standardSchedules",
                label: AcademicGrid.itemIssueTypes.standardSchedules.label,
                action: this.store.warningsConfig().standardSchedules.action,
                issues: standardSchedules,
            },
            {
                id: "missingFeaturePrefs",
                label: AcademicGrid.itemIssueTypes.missingFeaturePrefs.label,
                action: this.store.warningsConfig().missingFeaturePrefs.action,
                issues: missingFeaturePrefs,
            },
            {
                id: "missingPartitionPrefs",
                label: AcademicGrid.itemIssueTypes.missingPartitionPrefs.label,
                action: this.store.warningsConfig().missingPartitionPrefs.action,
                issues: missingPartitionPrefs,
            },
        ]).filter((issueType) => issueType.action !== "ignore");
        for (const issueType of types) AcademicGrid.sortIssues(issueType.issues);
        types.sort(
            (left, right) =>
                AcademicGrid.warningSeverities[right.action].severity -
                AcademicGrid.warningSeverities[left.action].severity,
        );

        return {
            data: { counts: AcademicGrid.getIssueCounts(types), types },
        };
    }

    private async getAvailabilityConflicts(
        changedItems: AcademicGrid.Item[],
        updateItems: boolean,
    ): Promise<Proto.ResultObj<{ locations: AcademicGrid.Issue[]; resources: AcademicGrid.Issue[] }, true>> {
        const availability = await this.checkAvailability(changedItems);
        if (availability.error) {
            alert("Something went wrong when checking for conflicts");
            return { error: true };
        }

        if (updateItems) {
            for (const item of changedItems) {
                item.academicGrid.issues.locationConflicts.set(false);
                item.academicGrid.issues.resourceConflicts.set(false);
            }
        }

        const profileToItem = S25Array.mapBy(changedItems, (item) => item.academicGrid.profileId);

        const locations = (availability.data?.locations ?? []).flatMap((spaceConflict) => {
            const item = profileToItem.get(spaceConflict.candidateEvDtProfileId);
            if (!item) return [];
            if (updateItems) item.academicGrid.issues.locationConflicts.set(true);
            const conflict: AcademicGrid.Issue = {
                item,
                time: AcademicGrid.getTimeString(item, this.userStore.is24Hours()),
                relationshipType: Relationships.typeId(spaceConflict.relnType),
                locations: [spaceConflict.objectId],
                events: [spaceConflict.conflictEventId],
            };
            return [conflict];
        });
        const resources = (availability.data?.resources ?? []).flatMap((resourceConflict) => {
            const item = profileToItem.get(resourceConflict.candidateEvDtProfileId);
            if (!item) return [];
            if (updateItems) item.academicGrid.issues.resourceConflicts.set(true);
            const conflict: AcademicGrid.Issue = {
                item,
                time: AcademicGrid.getTimeString(item, this.userStore.is24Hours()),
                resources: [resourceConflict.objectId],
                events: [resourceConflict.conflictEventId],
            };
            return [conflict];
        });

        return { data: { locations, resources } };
    }

    private getInstructorConflicts(items: readonly AcademicGrid.Item[]) {
        const scannedIds = new Set(items.map((item) => item.id));
        const instructorItems = items.filter((item) => {
            if (item.academicGrid.isLocationConflict) return false;
            return !!item.academicGrid.effective.position.instructors().length;
        });
        const is24Hours = this.userStore.is24Hours();
        const conflicts: AcademicGrid.Issue[] = [];

        for (const item of instructorItems) {
            const overlaps = this.store.getOverlappingInstructors(item);
            const itemConflicts = this.store.getInstructorConflicts(item, overlaps, is24Hours);
            for (const conflict of itemConflicts) {
                const partners = conflict.items!.slice(1).filter((other) => {
                    if (!scannedIds.has(other.id)) return true;
                    return String(item.id) < String(other.id);
                });
                if (!partners.length) continue;
                conflicts.push({ ...conflict, items: [item, ...partners] });
            }
        }

        return conflicts;
    }

    private getLocationUndersizedIssues(items: readonly AcademicGrid.Item[]): AcademicGrid.Issue[] {
        const locationData = this.store.locations();
        return items.flatMap((item) => {
            const undersizedIds = AcademicGrid.getUndersizedLocations({
                locations: item.academicGrid.effective.position.locations(),
                registeredHeadCount: item.academicGrid.registeredHeadCount,
                expectedHeadCount: item.academicGrid.expectedHeadCount,
                warningsConfig: this.store.warningsConfig(),
                mode: this.store.mode(),
                locationData,
            });
            const ratio = this.store.warningsConfig().locationUndersized.ratio;
            return undersizedIds.map((id) => ({
                type: "locationUndersized",
                item: item,
                time: AcademicGrid.getTimeString(item, this.userStore.is24Hours()),
                location: locationData[id]!,
                headCountLabel: AcademicGrid.ratioToHeadCountLabel(ratio),
                headCount: AcademicGrid.ratioToHeadCount(item.academicGrid, ratio),
            }));
        });
    }

    private getLocationOversizedIssues(items: readonly AcademicGrid.Item[]): AcademicGrid.Issue[] {
        const locationData = this.store.locations();
        return items.flatMap((item) => {
            const undersizedIds = AcademicGrid.getOversizedLocations({
                locations: item.academicGrid.effective.position.locations(),
                registeredHeadCount: item.academicGrid.registeredHeadCount,
                expectedHeadCount: item.academicGrid.expectedHeadCount,
                warningsConfig: this.store.warningsConfig(),
                mode: this.store.mode(),
                locationData,
            });
            const ratio = this.store.warningsConfig().locationOversized.ratio;
            return undersizedIds.map((id) => ({
                type: "locationOversized",
                item: item,
                time: AcademicGrid.getTimeString(item, this.userStore.is24Hours()),
                location: locationData[id]!,
                headCountLabel: AcademicGrid.ratioToHeadCountLabel(ratio),
                headCount: AcademicGrid.ratioToHeadCount(item.academicGrid, ratio),
            }));
        });
    }

    private getStandardScheduleIssues(items: readonly AcademicGrid.Item[]): AcademicGrid.Issue[] {
        if (!this.store.standardSchedule()) return [];
        if (this.store.warningsConfig().standardSchedules.action === "ignore") return [];
        const noFit = items.filter((item: AcademicGrid.Item) => item.academicGrid.issues.standardSchedules());
        return noFit.map((item) => ({
            type: "standardSchedules",
            item: item,
            time: AcademicGrid.getTimeString(item, this.userStore.is24Hours()),
        }));
    }

    private getMissingFeaturePrefsIssues(items: readonly AcademicGrid.Item[]): AcademicGrid.Issue[] {
        if (this.store.warningsConfig().missingFeaturePrefs.action === "ignore") return [];
        const locationData = this.store.locations();
        return items.flatMap((item) => {
            const missing = AcademicGrid.getMissingFeaturePrefs({
                locations: item.academicGrid.effective.position.locations(),
                mode: this.store.mode(),
                locationData,
                featurePreferences: item.academicGrid.featurePreferences,
            });
            return missing.map((location) => ({
                type: "missingFeaturePrefs",
                item: item,
                time: AcademicGrid.getTimeString(item, this.userStore.is24Hours()),
                location: locationData[location.id],
                missingPrefs: location.missingPrefs,
            }));
        });
    }

    private getMissingPartitionPrefsIssues(items: readonly AcademicGrid.Item[]): AcademicGrid.Issue[] {
        if (this.store.warningsConfig().missingPartitionPrefs.action === "ignore") return [];
        const locationData = this.store.locations();
        return items.flatMap((item) => {
            const locationIds = AcademicGrid.getMissingPartitionPrefs({
                locations: item.academicGrid.effective.position.locations(),
                mode: this.store.mode(),
                locationData,
                partitionPreferences: item.academicGrid.partitionPreferences,
                primaryOrganization: item.academicGrid.primaryOrganization,
            });
            return locationIds.map((id) => ({
                type: "missingPartitionPrefs",
                item: item,
                time: AcademicGrid.getTimeString(item, this.userStore.is24Hours()),
                location: locationData[id],
            }));
        });
    }

    @Bind
    private async onPickup(
        context: DowGrid.PickupContext<AcademicGrid.Item, AcademicGrid.ProjectionData>,
    ): Promise<boolean> {
        this.store.grabbedEntry.set({
            rowKey: AcademicGrid.getRowKeyFromPickupContext(this.store.mode(), context),
            entry: context.grabbedEntry,
        });
        // TODO MULTIPLAYER: Lock item on pick up
        return true;
    }

    @Throttle(300, true)
    private onDragChanged(context: DowGrid.DragChangedContext<AcademicGrid.Item>): void {
        // TODO MULTIPLAYER: Send update messages
        AriaLive.announce(this.getAriaLiveTime(context.primary), true);
    }

    private getAriaLiveTime(item: AcademicGrid.Item) {
        const startHour = item.academicGrid.effective.position.startHour();
        const time = S25Util.date.ariaTimeString(
            S25Util.date.toTimeStrFromHours(startHour, this.userStore.is24Hours()),
        );
        return `${time}`;
    }

    @Bind
    private async onDrop(
        context: Omit<DowGrid.DropContext<AcademicGrid.Item>, "candidates" | "origins">,
    ): Promise<DowGrid.DropResult<AcademicGrid.Item>> {
        const items = context.participants.filter((gridItem) => AcademicGrid.hasItemChanged(gridItem));
        if (items.length === 0) return false;

        const swap = await this.offerLocationSwap();
        if (swap === undefined) return this.rejectDrop(items); // User closed the modal
        if (swap) items.push(swap);

        const removedOrphans = await this.offerToRemoveOrphanedAlsoAssigns(items);
        if (removedOrphans === undefined) return this.rejectDrop(items); // User closed the modal

        const ok = await this.commitItems(items);
        if (!ok) return this.rejectDrop(items);

        // Report the swap partner so the grid folds it into onCommit and the undo/redo history too —
        // it never went through a drag session of its own.
        return swap ? { participants: [swap] } : true;
    }

    private rejectDrop(items: readonly AcademicGrid.Item[]) {
        this.clearCandidateOverrides(items);
        return false;
    }

    private async commitItems(items: readonly AcademicGrid.Item[]): Promise<boolean> {
        if (items.length === 0) return false;

        const { ok, updateIssues } = await this.validateItems(items);
        if (!ok) return this.rejectDrop(items);

        if (this.store.saveType() === "individual") {
            const resp = await this.saveItems(items);
            if (!!resp.error) {
                return this.rejectDrop(items); // Return item(s) to previous position
            }
            // TODO MULTIPLAYER: Send message that item moved
        } else if (this.store.saveType() === "bulk") {
            const changes = items.map((gridItem) => {
                const candidate = AcademicGrid.readCandidate(gridItem.academicGrid.effective);
                return {
                    gridId: this.grid()!.id,
                    eventId: gridItem.academicGrid.eventId,
                    profileId: gridItem.academicGrid.profileId,
                    pattern: candidate.position.dow,
                    startTime: `1900-01-01T${S25Util.date.toS25ISOTimeStrFromHours(candidate.eventStartHour)}`,
                    endTime: `1900-01-01T${S25Util.date.toS25ISOTimeStrFromHours(candidate.eventEndHour)}`,
                };
            });

            const resp = await GridsService.moveItems(this.grid()!.id, changes);
            if (resp.error) {
                alert(`Something went wrong when moving item${items.length === 1 ? "" : "s"}`);
                return this.rejectDrop(items); // Return item(s) to previous position
            }
        }

        updateIssues?.();

        // TODO MULTIPLAYER: Unlock on put down
        return true;
    }

    @Bind
    private onCommit(context: DowGrid.CommitContext<AcademicGrid.Item>): void {
        const items = context.participants;
        for (const item of items) AcademicGrid.commitCandidate(item);
        this.clearCandidateOverrides(items);
    }

    private async offerLocationSwap(): Promise<AcademicGrid.Item | false | undefined> {
        if (this.store.mode() !== "location") return false;

        const grabbedEntry = this.store.grabbedEntry();
        const item = grabbedEntry?.entry.source;
        const changedLocation = item?.academicGrid.changedLocation();
        if (!item || !changedLocation || changedLocation.old === "none" || changedLocation.new === "none") return false;

        const linkedIds = new Set(item.academicGrid.linkedItems.map((item) => item.id));
        const effectiveOccurrences = item.academicGrid.effective.occurrences();
        const conflicts = this.store.items().filter((other) => {
            if (other.id === item.id || linkedIds.has(other.id) || other.draggable?.() !== true) return false;

            const locations = other.academicGrid.effective.position.locations();
            const hasOld = locations.includes(<number>changedLocation.old);
            const hasNew = locations.includes(<number>changedLocation.new);
            if (hasOld || !hasNew) return false;

            const otherOccurrences = other.academicGrid.current().occurrences;
            return AcademicGrid.doOccurrencesOverlap(effectiveOccurrences, otherOccurrences);
        });
        if (conflicts.length !== 1) return false;

        const swap = conflicts[0];
        const ok = await this.offerSwapModal()?.open({
            templateData: {
                swap: {
                    sectionA: item.academicGrid.eventName,
                    sectionB: swap.academicGrid.eventName,
                    locationA: this.store.locations()[changedLocation.old]?.name,
                    locationB: this.store.locations()[changedLocation.new]?.name,
                },
            },
        });
        if (!ok) return <false | undefined>ok;

        swap.academicGrid.swappedLocations.set({ old: changedLocation.new, new: changedLocation.old });

        return swap;
    }

    // Check if user wants to remove any orphaned "also assign"s
    private async offerToRemoveOrphanedAlsoAssigns(items: AcademicGrid.Item[]): Promise<boolean | undefined> {
        const orphanedAlsoAssignIssues = AcademicGrid.getOrphanedAlsoAssignIssues(
            items,
            this.store.locationRelationships(),
            this.userStore.is24Hours(),
        );
        if (!!orphanedAlsoAssignIssues.counts.total) {
            const issues = orphanedAlsoAssignIssues.types[0].issues;
            const count = S25Array.count(issues, (issue) => issue.locations!.length);
            const remove = await this.removeOrphanedAlsoAssignModal()!.open({
                title: "Orphaned Also Assign Locations",
                templateData: { issues: orphanedAlsoAssignIssues, count },
            });
            if (remove) {
                for (const issue of orphanedAlsoAssignIssues.types[0].issues) {
                    issue.item.academicGrid.removedOrphanedAlsoAssignLocations.set(new Set(issue.locations!));
                }
            }
            return remove;
        }

        return true;
    }

    private async validateItems(
        items: readonly AcademicGrid.Item[],
    ): Promise<{ ok: boolean; updateIssues?: () => void }> {
        for (const item of items) {
            const message = this.validateAP(item);
            if (message) {
                alert(message);
                return { ok: false };
            }
        }

        const itemIds = new Set(items.map((item) => item.id));

        const issuesResult = await this.getIssues(items, false);
        if (issuesResult.error) return { ok: false };
        const issues = issuesResult.data!;
        const byType = <Record<AcademicGrid.IssueTypeId, AcademicGrid.IssueType>>(
            Object.fromEntries(issues.types.map((type) => [type.id, type]))
        );
        if (byType.instructorConflicts) {
            const instructorConflictCount = byType.instructorConflicts.issues.length;
            byType.instructorConflicts.issues = byType.instructorConflicts.issues.filter((conflict) =>
                conflict.items!.some((item) => itemIds.has(item.id)),
            );
            issues.counts.total += byType.instructorConflicts.issues.length - instructorConflictCount;
        }

        // If there are also-assign locations, ask if user wants to proceed
        const alsoAssignIssues = AcademicGrid.getAlsoAssignIssues(items, this.userStore.is24Hours());
        if (!!alsoAssignIssues.counts.total) {
            const count = S25Util.array.count(alsoAssignIssues.types[0].issues, (issue) => issue.locations!.length);
            const ok = await this.confirmIssuesModal()!.open({
                title: "Also Assign Locations",
                templateData: {
                    action: "confirm",
                    message: `This action would assign ${count} additional location${count > 1 ? "s" : ""}.`,
                    issues: alsoAssignIssues,
                },
            });
            if (!ok) return { ok: false };
        }

        const prevent = issues.types.some((type) => type.action === "prevent" && !!type.issues.length);
        const warn = issues.types.some((type) => type.action === "warn" && !!type.issues.length);
        if (!prevent && !warn) return { ok: true };

        const type: ModalConfig["type"] = prevent ? "acknowledge" : "confirm";
        if (issues.counts.total > 0) {
            const ok = await this.confirmIssuesModal()!.open({ type, templateData: { action: type, issues } });
            if (!ok || type === "acknowledge") return { ok: false };
        }

        const updateIssues = () => {
            // If we're going ahead with the move, update conflict info for location and resource conflicts
            // Other conflicts are updated separately in afterItemsMoved
            const locationConflicts = new Set(byType.locationConflicts.issues.map((loc) => loc.item.id));
            const resourceConflicts = new Set(byType.resourceConflicts.issues.map((res) => res.item.id));
            for (const item of items) {
                item.academicGrid.issues.locationConflicts.set(locationConflicts.has(item.id));
                item.academicGrid.issues.resourceConflicts.set(resourceConflicts.has(item.id));
            }
        };

        return { ok: true, updateIssues: updateIssues };
    }

    private validateAP(item: AcademicGrid.Item): string | undefined {
        // Check AP
        const data = AcademicGrid.readCandidate(item.academicGrid.effective);
        for (const occ of data.occurrences) {
            const start = S25Util.date.setHours(S25Util.date.parse(occ.start), data.position.startHour);
            const end = S25Util.date.setHours(S25Util.date.parse(occ.end), data.position.endHour);

            const locationAp = occ.locations.map((loc) => loc.ap);
            const locationPerm = this.checkPerms({ start, end, perms: locationAp, timezone: this.timezone() });
            if (occ.locations?.length && !AvailService.isPermEnoughTo("assign", locationPerm)) {
                return `Insufficient perms to assign location(s) to designated time`;
            }

            const resourceAp = occ.resources.map((res) => res.ap);
            const resourcePerm = this.checkPerms({ start, end, perms: resourceAp, timezone: this.timezone() });
            if (occ.resources?.length && !AvailService.isPermEnoughTo("assign", resourcePerm)) {
                return `Insufficient perms to assign resource(s) to designated time`;
            }
        }

        return;
    }

    private checkPerms(options: {
        start: Date;
        end: Date;
        perms: EventPerm[];
        timezone: { instance: string; user: string };
    }) {
        const finalPerms: Perms[] = options.perms.map((perm) => {
            if (!perm) return { assign: "notSet", unassign: "notSet" };
            return AvailService.coalescePerms(perm, options.start, options.end, options.timezone);
        });
        return AvailService.lowestPerm(finalPerms);
    }

    @Bind
    protected async downloadIssues() {
        const sheets: Sheet[] = [];

        // Create summary sheet
        const summary: SheetEntry[] = [];
        sheets.push({ name: "Summary", data: summary });

        const addIssueType = (issueType: AcademicGrid.ItemIssueType, issues: SheetEntry[] | undefined) => {
            if (this.store.warningsConfig()[issueType].action === "ignore" || !issues?.length) return;
            const label = AcademicGrid.itemIssueTypes[issueType].label + "s";
            sheets.push({
                name: AcademicGrid.itemIssueTypes[issueType].label + "s",
                data: issues,
            });
            summary.push({
                "": label,
                Issues: issues.length,
                Severity: AcademicGrid.warningSeverities[this.store.warningsConfig()[issueType].action].label,
            });
        };

        // Create individual sheets for each issue type
        const byType = <Record<AcademicGrid.IssueTypeId, AcademicGrid.IssueType>>(
            Object.fromEntries(this.issues.types.map((type) => [type.id, type]))
        );

        addIssueType(
            "standardSchedules",
            byType.standardSchedules?.issues.map((issue) => ({
                "Meeting Pattern": issue.item.academicGrid.effective.position.dow(),
                Time: issue.time,
                Section: issue.item.academicGrid.eventName,
            })),
        );

        addIssueType(
            "instructorConflicts",
            byType.instructorConflicts?.issues.map((conflict) => ({
                Instructor: conflict.item.academicGrid.effective.instructors()[0]?.name,
                "Meeting Pattern": conflict.item.academicGrid.effective.position.dow(),
                Time: conflict.time,
                Sections: conflict.items!.map((item) => item.academicGrid.eventName).join("; "),
            })),
        );

        if (this.store.saveType() === "bulk") {
            addIssueType(
                "locationConflicts",
                byType.locationConflicts?.issues.map((conflict) => ({
                    "Meeting Pattern": conflict.item.academicGrid.effective.position.dow(),
                    Time: conflict.time,
                    Section: conflict.item.academicGrid.eventName,
                })),
            );

            addIssueType(
                "resourceConflicts",
                byType.resourceConflicts?.issues.map((conflict) => ({
                    "Meeting Pattern": conflict.item.academicGrid.effective.position.dow(),
                    Time: conflict.time,
                    Section: conflict.item.academicGrid.eventName,
                })),
            );
        }

        const undersizedRatio = this.store.warningsConfig().locationUndersized.ratio;
        const undersizedHeadCountLabel = AcademicGrid.ratioToHeadCountLabel(undersizedRatio);
        addIssueType(
            "locationUndersized",
            byType.locationUndersized?.issues.map((c) => {
                const headCount = AcademicGrid.ratioToHeadCount(c.item.academicGrid, undersizedRatio);
                const maxCapacity = c.location?.maxCapacity ?? 0;
                const fillRatio = (headCount / maxCapacity) * 100;
                return {
                    "Meeting Pattern": c.item.academicGrid.effective.position.dow(),
                    Time: c.time,
                    Section: c.item.academicGrid.eventName,
                    Location: c.location!.name,
                    [undersizedHeadCountLabel]: headCount,
                    Capacity: c.location?.maxCapacity ?? 0,
                    "Fill Ratio": `${Math.round(fillRatio)}%`,
                };
            }),
        );

        const oversizedRatio = this.store.warningsConfig().locationOversized.ratio;
        const oversizedHeadCountLabel = AcademicGrid.ratioToHeadCountLabel(oversizedRatio);
        addIssueType(
            "locationOversized",
            byType.locationOversized?.issues.map((c) => {
                const headCount = AcademicGrid.ratioToHeadCount(c.item.academicGrid, oversizedRatio);
                const maxCapacity = c.location?.maxCapacity ?? 0;
                const fillRatio = (headCount / maxCapacity) * 100;
                return {
                    "Meeting Pattern": c.item.academicGrid.effective.position.dow(),
                    Time: c.time,
                    Section: c.item.academicGrid.eventName,
                    Location: c.location!.name,
                    [oversizedHeadCountLabel]: AcademicGrid.ratioToHeadCount(c.item.academicGrid, oversizedRatio),
                    Capacity: c.location?.maxCapacity ?? 0,
                    "Fill Ratio": `${Math.round(fillRatio)}%`,
                };
            }),
        );

        addIssueType(
            "missingFeaturePrefs",
            byType.missingFeaturePrefs?.issues.map((issue) => ({
                "Meeting Pattern": issue.item.academicGrid.effective.position.dow(),
                Time: issue.time,
                Section: issue.item.academicGrid.eventName,
                "Missing Features": issue.missingPrefs!.join(", "),
            })),
        );

        addIssueType(
            "missingPartitionPrefs",
            byType.missingPartitionPrefs?.issues.map((issue) => ({
                "Meeting Pattern": issue.item.academicGrid.effective.position.dow(),
                Time: issue.time,
                Section: issue.item.academicGrid.eventName,
                Location: issue.location!.name,
            })),
        );

        summary.push({ "": "Total", Issues: S25Util.array.count(summary, (row) => <number>row.Issues) });

        await SpreadSheetService.downloadExcelMulti(sheets, `${this.grid()!.name} Issues.xlsx`);
    }

    private async checkAvailability(
        items: AcademicGrid.Item[],
    ): Promise<ResultObj<AcademicGrid.availability.Conflicts>> {
        const events: AvailabilityBatchRequest["events"] = [];
        for (const item of items) {
            const newRoomIdToOrigRoomId = this.vacatedRoomIdByNewRoomId(item);
            const reservations: AvailabilityBatchRequest["events"][number]["reservations"] = [];
            for (const [occIndex, occ] of S25Array.enumerate(item.academicGrid.effective.occurrences() || [])) {
                const additionalTimeDates = AcademicGrid.getOccurrenceDatesWithAdditionalTime(item, occ);
                const spaces = (occ.locations || []).map((location) => ({
                    roomId: location.id,
                    origRoomId: newRoomIdToOrigRoomId.get(location.id) ?? location.id,
                    sharedUse: location.shared,
                }));
                const resources = (occ.resources || []).map((resource) => ({
                    resourceId: resource.id,
                    origResourceId: resource.id,
                    quantity: resource.quantity,
                }));
                if (!spaces.length && !resources.length) continue;
                reservations.push({
                    rsrvId: item.academicGrid.original().occurrences[occIndex]?.id ?? -1,
                    startDt: additionalTimeDates.start,
                    endDt: additionalTimeDates.end,
                    spaces,
                    resources,
                });
            }
            if (reservations.length) {
                events.push({
                    eventId: item.academicGrid.eventId,
                    profileId: item.academicGrid.profileId,
                    reservations,
                });
            }
        }

        if (!events.length) return { data: { locations: [], resources: [] } };
        const conflicts = await SpaceService.checkConflicts(
            { events },
            {
                allowShared: true,
                allowBlackoutOverride: true,
                allowClosedHoursOverride: true,
                allowBlockedByOverride: false,
            },
        );
        if (!!conflicts.error) return { error: conflicts.error };

        return { data: AcademicGrid.availability.extractConflicts(conflicts.data!) };
    }

    private vacatedRoomIdByNewRoomId(item: AcademicGrid.Item): Map<number, number> {
        const currentLocations = item.academicGrid.current().position.locations;
        const newLocations = item.academicGrid.effective.position.locations();
        const vacatedRoomIds = currentLocations.filter((locationId) => !newLocations.includes(locationId));
        const addedRoomIds = newLocations.filter((locationId) => !currentLocations.includes(locationId));

        return new Map(addedRoomIds.map((roomId, index) => [roomId, vacatedRoomIds[index] ?? roomId]));
    }

    private async saveItems(items: readonly AcademicGrid.Item[]) {
        const missingOccIds = S25Util.array.count(items, (item) => {
            const diff =
                item.academicGrid.effective.occurrences().length - item.academicGrid.original().occurrences.length;
            return diff <= 0 ? 0 : diff;
        });
        let newOccIds: number[] = [];
        if (missingOccIds > 0) newOccIds = await ReservationService.generateReservationIdsList(missingOccIds);
        const newOccId = () => newOccIds.pop()!;

        const events = items.map((item) => AcademicGrid.getItemPayload(item, newOccId));
        const result = await this.eventBulkSaveService.save({
            events,
            allowShared: true,
            allowBlackoutOverride: true,
            allowClosedHoursOverride: true,
            allowBlockedByOverride: true,
        });

        const response = result.data;
        const httpErrorBody = result.error?.data;
        const denials = response?.denials?.length ? response.denials : httpErrorBody?.denials;
        const conflict = response?.reservationSaveResponse?.hasConflict || response?.error === "conflict";
        const failed = !!result.error || response?.success === false || !!denials?.length || conflict;

        if (!failed) {
            AcademicGrid.updateOriginalsWithCandidates(items);
        }

        const warningMessages = [...(response?.warnings || []), ...(httpErrorBody?.warnings || [])].filter(Boolean);
        const denialMessages = (denials || []).map((denial) => denial.reason).filter(Boolean) as string[];
        const conflictMessage =
            conflict && !denialMessages.length
                ? response?.error || "One or more reservations conflict with existing assignments"
                : undefined;
        const genericError =
            failed && !denialMessages.length && !conflictMessage && !warningMessages.length
                ? response?.error || httpErrorBody?.error || S25Error.extract(result.error) || "Save failed"
                : undefined;

        if (warningMessages.length || denialMessages.length || conflictMessage || genericError) {
            const title = failed ? "Error" : "Warning";
            const item = items[0];
            const time = item ? AcademicGrid.getTimeString(item, this.userStore.is24Hours()) : "";
            const types: AcademicGrid.IssueType[] = [
                {
                    id: "saveMessages",
                    label: AcademicGrid.saveIssueTypes.saveMessages.label,
                    action: "warn",
                    issues: warningMessages.length ? [{ item: item!, time, messages: warningMessages }] : [],
                },
                {
                    id: "saveErrors",
                    label: AcademicGrid.saveIssueTypes.saveErrors.label,
                    action: "warn",
                    issues:
                        denialMessages.length || conflictMessage || genericError
                            ? [
                                  {
                                      item: item!,
                                      time,
                                      message: genericError || conflictMessage,
                                      messages: [
                                          ...denialMessages,
                                          ...(conflictMessage ? [conflictMessage] : []),
                                          ...(genericError && !denialMessages.length ? [genericError] : []),
                                      ].filter(Boolean) as string[],
                                  },
                              ]
                            : [],
                },
            ];
            const issues: AcademicGrid.Issues = {
                counts: AcademicGrid.getIssueCounts(types),
                types,
            };
            await this.saveMessagesModal().open({ title, templateData: { issues } });
        }

        return { error: failed ? result.error || response || true : undefined, data: response };
    }

    @Bind
    protected async save(): Promise<void> {
        // TODO MULTIPLAYER: Lock
        // Get changes
        const changes = await GridsService.getChanges(this.grid()!.id);

        if (changes.error) {
            alert("Something went wrong");
            return;
        }
        if (!changes.data?.length) {
            alert("No changes to apply");
            return;
        }

        // Map profile ID to item
        const profileToItem = S25Util.array.mapBy(this.store.items(), (item) => item.academicGrid.profileId);

        // Apply changes
        const changedItems: AcademicGrid.Item[] = [];
        for (const change of changes.data) {
            const item = profileToItem.get(change.profileId);
            if (!item) continue;
            AcademicGrid.applyChange(item, change);
            if (AcademicGrid.hasItemChanged(item)) changedItems.push(item);
        }

        // TODO BULK SAVE: Check all issues (including instructor)
        // TODO BULK SAVE: Require all location/resource conflicts be resolved
        // TODO BULK SAVE: Ask if they want to continue if only instructor conflicts
        // TODO BULK SAVE: Show errors from service

        // Check availability
        this.checkAvailability(changedItems); // TODO BULK SAVE: Handle issues

        this.saveItems(changedItems);

        // TODO BULK SAVE: Telemetry

        await GridsService.clearChanges(this.grid()!.id);
        this.dowGridRef()?.clearHistory();
        // TODO BULK SAVE: Refresh?

        // TODO MULTIPLAYER: Unlock
    }

    private async disableWhile(func: () => any) {
        this.elementRef.nativeElement.classList.add("disableGrid");
        await S25Util.delay(0); // Wait for CSS to apply class styles
        try {
            func();
        } finally {
            this.elementRef.nativeElement.classList.remove("disableGrid");
        }
    }

    private loadWhile<Args extends unknown[], Result>(
        func: (...args: Args) => Promise<Result>,
    ): (...args: Args) => Promise<Result> {
        return async (...args: Args) => {
            this.isLoading.set(true);
            try {
                return await func(...args);
            } finally {
                this.isLoading.set(false);
            }
        };
    }
}
